import {
  Box,
  Collapse,
  ExpandMoreRoundedIcon,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Text,
  styled,
  type ListItemButtonProps,
} from '@shared/components';
import { Link, matchPath, useLocation, useMatch, useResolvedPath } from 'react-router-dom';

import type { MenuItem } from '../../domains';

import TooltipMenu from './TooltipMenu';

const SmallDot = styled(Box)(({ theme }) => ({
  width: 24,
  height: 24,
  backgroundColor: 'transparent',
  position: 'relative',
  '&::before': {
    content: '""',
    width: 4,
    height: 4,
    borderRadius: theme.shape.rounded('50%'),
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: theme.palette.grey[600],
  },
}));

type NavMenuItemProps = Pick<MenuItem, 'label' | 'icon' | 'link' | 'subMenu'> & {
  collapsible?: boolean;
  disableActiveHighlight?: boolean;
  onToggleSubMenu?: () => void;
  open?: boolean;
  minified?: boolean;
} & ListItemButtonProps;

function NavMenuItem(props: Readonly<NavMenuItemProps>) {
  const {
    icon: iconProp,
    label: labelProp,
    link = '',
    open: isOpen = false,
    subMenu,
    collapsible: isCollapsible,
    disableActiveHighlight = false,
    onToggleSubMenu,
    minified: isMinified,
  } = props;

  const { pathname: currentPath } = useLocation();

  const target = useResolvedPath(link);

  // If not match with user's current or nested route, route's match object will be null.
  const targetPageMatch = useMatch({ path: `${target.pathname}` });
  const targetOrNestedPageMatch = useMatch({ path: `${target.pathname}/*` });

  const isAlreadyOnTargetPage = !!targetPageMatch;
  const isAlreadyOnTargetOrNestedPage = !!targetOrNestedPageMatch;

  let icon;
  let label;
  let tooltipContent;

  if (iconProp) {
    icon = <ListItemIcon>{iconProp}</ListItemIcon>;
  } else {
    icon = (
      <ListItemIcon
        sx={
          isAlreadyOnTargetOrNestedPage
            ? {
                '& > div::before': {
                  backgroundColor: (theme) => theme.palette.primary.main,
                  width: 8,
                  height: 8,
                },
              }
            : {}
        }
      >
        <SmallDot />
      </ListItemIcon>
    );
  }

  if (labelProp && !isMinified) {
    if (typeof labelProp === 'string') {
      const isBold = disableActiveHighlight && isAlreadyOnTargetOrNestedPage;
      label = (
        <Text variant='body2' fontWeight={isBold ? 600 : undefined}>
          {labelProp}
        </Text>
      );
    } else {
      label = labelProp;
    }
  }

  // used for rendering TooltipMenu
  const isAccordionMenu = !!subMenu;
  const selectedSubMenu = subMenu?.find((menu) => {
    const targetIsSubMenu = matchPath(`${menu.link}`, currentPath);
    const targetIsUnderSubMenu = matchPath(`${menu.link}/*`, currentPath);

    return targetIsSubMenu || targetIsUnderSubMenu;
  });
  const isSelectingSubMenu = !!selectedSubMenu;

  const handleToggleSubMenu = (
    event: React.KeyboardEvent<SVGSVGElement> | React.MouseEvent<SVGSVGElement>,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    onToggleSubMenu?.();
  };

  if (isAccordionMenu) {
    // tooltip with submenu
    tooltipContent = (
      <>
        <ListItemText>
          <Text variant='body1' fontWeight={600}>
            {labelProp}
          </Text>
        </ListItemText>
        {subMenu.map((item) => (
          <ListItemButton
            key={item.label as string}
            selected={selectedSubMenu?.link === item.link}
            component={Link}
            to={item.link as string}
            sx={{ width: 200 }}
            onClick={
              currentPath === item.link
                ? (e: React.MouseEvent<HTMLAnchorElement>) => e.preventDefault()
                : undefined
            }
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <Text variant='body2'>{item.label}</Text>
          </ListItemButton>
        ))}
      </>
    );
  } else {
    // basic tooltip
    tooltipContent = (
      <ListItemText>
        <Text variant='body1' fontWeight={600}>
          {labelProp}
        </Text>
      </ListItemText>
    );
  }

  return isCollapsible ? (
    <Collapse in={!isMinified}>
      <ListItemButton
        selected={!disableActiveHighlight && isAlreadyOnTargetOrNestedPage}
        component={Link}
        to={link}
        onClick={
          isAlreadyOnTargetPage
            ? (e: React.MouseEvent<HTMLAnchorElement>) => e.preventDefault()
            : undefined
        }
      >
        {icon}
        {label}
      </ListItemButton>
    </Collapse>
  ) : (
    <TooltipMenu
      title={isMinified ? tooltipContent : ''}
      leaveDelay={isAccordionMenu ? 150 : 0}
      enterTouchDelay={isAccordionMenu ? 0 : 700}
      leaveTouchDelay={isAccordionMenu ? 3000 : 1500}
    >
      <ListItemButton
        selected={
          isAccordionMenu
            ? isSelectingSubMenu || isAlreadyOnTargetOrNestedPage
            : isAlreadyOnTargetOrNestedPage
        }
        sx={{ borderRadius: (theme) => theme.shape.rounded(1.5) }}
        component={Link}
        to={link}
        disableRipple={isAccordionMenu && isMinified}
        onClick={
          isAlreadyOnTargetPage
            ? (e: React.MouseEvent<HTMLAnchorElement>) => e.preventDefault()
            : undefined
        }
      >
        {icon}
        {label}
        {isAccordionMenu ? (
          <ExpandMoreRoundedIcon
            role='button'
            tabIndex={isMinified ? -1 : 0}
            onClick={handleToggleSubMenu}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                handleToggleSubMenu(event);
              }
            }}
            sx={{
              display: isMinified ? 'none' : 'block',
              ml: 'auto',
              outline: 'none',
              transform: `rotate(-${isOpen ? 180 : 0}deg)`,
              transition: (theme) =>
                `transform ${theme.transitions.duration.shortest}ms ${theme.transitions.easing.easeInOut}`,
            }}
          />
        ) : null}
      </ListItemButton>
    </TooltipMenu>
  );
}

export type { NavMenuItemProps };
export default NavMenuItem;
